import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import { isEmpty } from '@rainbow-modules/validation';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Close from 'components/icons/close';
import Gradient from 'components/Gradient';
import FlagIcon from 'components/icons/flag';
import CallDirectionIcon from 'components/icons/callDirection';
import useSearchFilters from 'hooks/useSearchFilters';
import StatusIcon from 'components/icons/status';
import Calendar from 'components/icons/calendar';
import tryParseNumber from 'data/services/string/parseNumber';
import Accordion from '../../Opportunities/View/accordion';
import {
    FilterHeader,
    Title,
    FiltersContainer,
    FiltersContent,
} from '../../Opportunities/View/styled';
import {
    TagIcon, ClockIcon, StyledDatePickerFilter, DurationFilter,
    StyledCallTypeFilter, StyledCallStatusFilter,
    StyledCallTagsFilter, StyledCallFlagsFilter,
} from './styled';

interface Params {
    agentId: string;
    onRequestClose?: () => void;
}

const filterNames = ['date', 'duration', 'direction', 'status', 'tags', 'flags'];

const Filters = ({
    agentId,
    onRequestClose = () => {},
}: Params) => {
    const {
        values,
        getValue: getFilterValue,
        setValue: setFilterValue,
        clear: clearFilters,
    } = useSearchFilters({ filterNames });

    const activeFiltersCount = useMemo(
        () => filterNames.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [getFilterValue],
    );

    const createdAtFilterValue = useMemo(
        () => {
            const { date } = values;
            if (!date) return undefined;
            return String(date).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeCreatedAt = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'date',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const durationFilterValue = useMemo(
        () => {
            const { duration } = values;
            if (duration) {
                return String(duration).split(',').reduce(
                    (
                        result: Array<number | null>,
                        value: string,
                    ) => [...result, tryParseNumber(value)],
                    [],
                );
            }

            return undefined;
        },
        [values],
    );

    const handleChangeDurationFilter = useCallback(
        (newFilterValue: Array<number | null>) => {
            setFilterValue(
                'duration',
                newFilterValue.every((value) => isEmpty(value))
                    ? null
                    : newFilterValue.map((value) => value || '').join(','),
            );
        },
        [setFilterValue],
    );

    const callTypeFilterValue = values.direction !== null ? String(values.direction).split(',') : null;

    const handleChangeCallTypeFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'direction',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const statusFilterValue = values.status !== null ? String(values.status).split(',') : null;

    const handleChangeStatusFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'status',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const flagsFilterValue = values.flags !== null ? String(values.flags).split(',') : null;

    const handleChangeFlagsFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'flags',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const tagsFilterValue = values.tags !== null ? String(values.tags).split(',') : null;

    const handleChangeTagsFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'tags',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    return (
        <FiltersContainer>
            <FilterHeader>
                <Title>
                    Filters
                    {' '}
                    <RenderIf isTrue={activeFiltersCount > 0}>
                        (
                        {activeFiltersCount}
                        )
                    </RenderIf>
                </Title>
                <div>
                    <Button
                        label="Clear"
                        variant="base"
                        size="small"
                        disabled={!activeFiltersCount}
                        borderRadius="semi-square"
                        onClick={clearFilters}
                    />
                    <ButtonIcon
                        borderRadius="semi-square"
                        size="small"
                        icon={<Close />}
                        onClick={onRequestClose}
                    />
                </div>
            </FilterHeader>
            <FiltersContent>
                <Accordion icon={<Calendar />} label="Date" name="date">
                    <StyledDatePickerFilter
                        onChange={handleChangeCreatedAt}
                        value={createdAtFilterValue}
                    />
                </Accordion>
                <Accordion icon={<ClockIcon />} label="Duration (in seconds)" name="duration">
                    <DurationFilter
                        value={durationFilterValue}
                        onChange={handleChangeDurationFilter}
                    />
                </Accordion>
                <Accordion icon={<CallDirectionIcon />} label="Direction" name="direction">
                    <StyledCallTypeFilter
                        value={callTypeFilterValue}
                        onChange={handleChangeCallTypeFilter}
                    />
                </Accordion>
                <Accordion icon={<StatusIcon />} label="Status" name="status">
                    <StyledCallStatusFilter
                        value={statusFilterValue}
                        onChange={handleChangeStatusFilter}
                    />
                </Accordion>
                <Accordion icon={<FlagIcon />} label="Flags" name="flags">
                    <StyledCallFlagsFilter
                        value={flagsFilterValue}
                        onChange={handleChangeFlagsFilter}
                    />
                </Accordion>
                <Accordion icon={<TagIcon />} label="Tags" name="tags">
                    <StyledCallTagsFilter
                        agentId={agentId}
                        value={tagsFilterValue}
                        onChange={handleChangeTagsFilter}
                        entity="call"
                    />
                </Accordion>
            </FiltersContent>
            <Gradient position="bottom" />
        </FiltersContainer>
    );
};

export default Filters;
